import React, { useEffect, useState, useCallback } from "react";
import { ShieldAlert, HelpCircle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const TABS = [
  { key: "objection", label: "Objection Risk Review" },
  { key: "crossref", label: "Cross-Reference Questions" },
];

export default function ReviewCenter({ open, onClose }) {
  const [tab, setTab] = useState("objection");
  const [risk, setRisk] = useState(null);
  const [xref, setXref] = useState(null);

  const load = useCallback(async () => {
    if (!open) return;
    try {
      const r = await api.get("/review-center/objection-risk");
      setRisk(r.data);
      const x = await api.get("/review-center/cross-reference");
      setXref(x.data);
    } catch (e) { console.error("review center load failed", e); }
  }, [open]);
  useEffect(() => { load(); }, [load]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="review-center">
        <DialogHeader>
          <DialogTitle className="font-head font-medium tracking-tight flex items-center gap-2">
            <ShieldAlert className="h-5 w-5" style={{ color: "var(--ek-primary)" }} /> Review Center
          </DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
            Optional, neutral documentation checks. These never determine admissibility, fault, or legal outcome.
          </DialogDescription>
        </DialogHeader>

        <div className="inline-flex rounded-md border overflow-hidden mb-2" style={{ borderColor: "var(--ek-border)" }}>
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`review-tab-${t.key}`}
              className="px-3 py-1.5 text-xs font-medium transition-colors"
              style={{ background: tab === t.key ? "var(--ek-primary)" : "transparent", color: tab === t.key ? "#fff" : "var(--ek-text-secondary)" }}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="max-h-[55vh] overflow-y-auto pr-1">
          {tab === "objection" && (
            <div className="space-y-2" data-testid="review-objection">
              {(risk?.items || []).length === 0 ? (
                <p className="text-sm flex items-center gap-2 py-4" style={{ color: "var(--ek-text-muted)" }}><CheckCircle2 className="h-4 w-4" style={{ color: "var(--ek-secure)" }} /> No procedural-risk profiles matched your evidence.</p>
              ) : (risk.items.map((it) => (
                <div key={it.evidence_id} className="border rounded-md p-3" style={{ borderColor: "rgba(228,179,74,0.4)" }} data-testid={`risk-${it.evidence_id}`}>
                  <p className="text-sm font-medium" data-private>{it.filename}</p>
                  {it.risks.map((rk, i) => (
                    <div key={i} className="mt-1">
                      <p className="ek-mono-label" style={{ color: "#E4B34A" }}>{rk.category}</p>
                      <p className="text-xs" style={{ color: "var(--ek-text-secondary)" }}>{rk.statement}</p>
                    </div>
                  ))}
                </div>
              )))}
              {risk?.disclaimer && <p className="text-[11px] mt-1" style={{ color: "var(--ek-text-muted)" }}>{risk.disclaimer}</p>}
            </div>
          )}

          {tab === "crossref" && (
            <div className="space-y-2" data-testid="review-crossref">
              {(xref?.questions || []).length === 0 ? (
                <p className="text-sm flex items-center gap-2 py-4" style={{ color: "var(--ek-text-muted)" }}><CheckCircle2 className="h-4 w-4" style={{ color: "var(--ek-secure)" }} /> Your documentation covers the expected categories for this case type.</p>
              ) : (xref.questions.map((q, i) => (
                <div key={i} className="border rounded-md p-3 flex items-start gap-2" style={{ borderColor: "var(--ek-border)" }} data-testid={`xref-${i}`}>
                  <HelpCircle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--ek-primary)" }} />
                  <div>
                    <p className="ek-mono-label">{q.category}</p>
                    <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>{q.question}</p>
                  </div>
                </div>
              )))}
              {xref?.disclaimer && <p className="text-[11px] mt-1" style={{ color: "var(--ek-text-muted)" }}>{xref.disclaimer}</p>}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
